<template lang="pug">
.requestCall-card.position-relative
  EcreativMoleculesLocationBaseCard.requestCall(
    variant="base-card--location"
    :expandable="false"
  )
    template(#header)
      .d-flex.flex-column.gap-1.mb-0.px-4.pt-3.pb-3(
        role="button"
        tabindex="0"
        @click.stop="emit('toggle-open', location.id)"
        @keydown.enter.stop.prevent="emit('toggle-open', location.id)"
        @keydown.space.stop.prevent="emit('toggle-open', location.id)"
        :aria-expanded="String(isOpen)"
        :aria-controls="`panel-${location.id}`"
      )
        EcreativArchetypesHeading.mb-1(tag="h3" size="5") {{ `${location.name_to_display}` }}
        EcreativArchePrimitivesParagraph.m-0(size="6" context="info") {{ location.location || '' }}
        EcreativArchePrimitivesParagraph.mb-0(size="7" context="info") {{ formattedDistance }}

    .d-flex.flex-column.gap-1.mb-0.px-4.pt-2.pb-4(
      :id="`panel-${location.id}`"
      v-if="isOpen"
    )
      EcreativArchePrimitivesParagraph.mb-1(size="7" context="dark" fontWeight="bold") Introduction
      EcreativArchePrimitivesParagraph(size="7" context="dark") {{ location.introduction || '—' }}

      EcreativArchePrimitivesParagraph.mb-1.mt-2(size="7" context="dark" fontWeight="bold") What to Expect
      EcreativArchePrimitivesParagraph(size="7" context="dark") {{ location.what_to_expect || '—' }}

      EcreativArchePrimitivesParagraph.mb-1.mt-2(size="7" context="dark" fontWeight="bold") Location
      EcreativArchePrimitivesParagraph(size="7" context="dark") {{ location.location || '—' }}

      .requestCall__line.pt-3.mt-3
        EcreativArchePrimitivesParagraph(size="7" context="dark")
          | Choose how you'd like to contact the Imam:

      EcreativMoleculesLocationCardFooter
        EcreativArchetypesInteractiveArchetype.justify-content-center(
          componentType="Interactive"
          text="Send Message"
          fontWeight="bold"
          context="dark"
          @click.stop="emit('send-message', location)"
        )
        EcreativArchetypesInteractiveArchetype.justify-content-center.mb-3(
          componentType="Interactive"
          text="Request a Call Back"
          fontWeight="bold"
          context="dark"
          @click.stop="emit('request-callback', location)"
        )

  .card-popup-anchor(:id="`card-popup-${location.id}`")
</template>

<script setup lang="ts">
/* eslint-disable */
import { computed } from 'vue';

interface Location {
  id: string | number;
  name: string;
  name_to_display: string;
  department?: string;
  distance?: number | string;
  introduction?: string;
  expectText?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  postcode?: string;
  associated_mosque?: string;
}

const props = defineProps<{
  location: Location;
  openId: string | number | null;
}>();

const emit = defineEmits<{
  (e: 'toggle-open', id: string | number): void;
  (e: 'send-message', location: Location): void;
  (e: 'request-callback', location: Location): void;
}>();

const isOpen = computed(
  () => String(props.openId) === String(props.location.id),
);

const formattedDistance = computed(() => {
  const d = props.location.distance;
  if (d == null || d === '') return '';
  const num = typeof d === 'string' ? Number(d) : d;
  return Number.isFinite(num) ? `${num.toFixed(1)} mi` : '';
});

const fullAddress = computed(() =>
  [
    props.location.address_line_1,
    props.location.address_line_2,
    props.location.address_line_3,
    props.location.postcode,
  ]
    .filter(Boolean)
    .join(', '),
);
</script>

<style scoped lang="scss">
$block: '.requestCall';

#{$block} {
  $block: &;

  #{$block}__line {
    border-top: 1px solid #878787;
  }
}

/* The wrapper ensures positioning context */
.requestCall-card {
  position: relative;
  border-radius: inherit;
}

/* Full-card absolute anchor that the parent teleports into */
.card-popup-anchor {
  position: absolute;
  z-index: 10;

  /* Don’t block interactions when empty */
  pointer-events: none;
  inset: 0;
  border-radius: inherit;
}

/* When the overlay exists inside, enable interactions on the anchor */
.card-popup-anchor:has(.card-overlay) {
  pointer-events: auto;
}
</style>
